import { tool, type ToolDescriptor } from "../tool";

/**
 * The six state keys `docs/tool-surface.md` constrains `giraffe_state` to.
 *
 * ⚠️ `rawSections` is the only GEO KEY here, and that matters. Verified
 * 2026-09-09 off the shipped SDK runtime: `get()` routes the five geo keys
 * (`rawSections`, `bakedSections`, `projects`, `selected`, `lensSelection`)
 * through `toGeoJSON`, whose `Object.values(v || {})` returns a well-formed
 * EMPTY FeatureCollection for a key that has never arrived. So for
 * `rawSections`, "the host has not pushed this yet" and "this project has no
 * sections" are byte-identical — and the handler MUST check presence on
 * `attr` rather than emptiness of the result. The other five keys are plain
 * reads and are `undefined` until first push, which is distinguishable.
 */
export const STATE_KEYS = [
  "project",
  "projectLayers",
  "layerTree",
  "selectedProjectApp",
  "rawSections",
  // Every app placed in the project, by app id (#41). Every app but "1" arrives
  // stripped to `{ public }`; app "1" arrives whole and holds the analytics.
  "projectAppsByAppID",
] as const;

export const GEO_STATE_KEYS: readonly string[] = ["rawSections"];

// Named `giraffeStateTool`, not `giraffeState`, deliberately. The copy of this
// file lands in apps that carry the bridge, where `giraffeState` is the SDK's own
// well-known export — two different things under one name in one repo is how a
// wrong import compiles clean.
export const giraffeStateTool: ToolDescriptor = tool({
  name: "giraffe_state",
  description:
    "Read one pushed Giraffe host state key for the current project. Returns the key's " +
    "value, plus whether the host has pushed it yet — an empty result and a not-yet-" +
    "loaded result are different answers and are reported differently.",
  inputSchema: {
    type: "object",
    properties: {
      key: { type: "string", description: "Which state key to read", enum: STATE_KEYS },
    },
    required: ["key"],
    additionalProperties: false,
  },
  hostRpcNames: [],
  settlesBeforeResolving: true,
  canAwaitUser: false,
});
